import { FaArrowLeft, FaLanguage, FaRobot, FaChartLine, FaGlobe } from 'react-icons/fa6';
import { motion } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';
import { useRedirect } from '../../hooks/useRedirect';
import pathnames from '../../routes/index.route';
import logo from '../../assets/images/logo.png';
import Button from '../../components/Button';

const SignIn = () => {
  const { loginWithRedirect } = useAuth0();
  const navigate = useRedirect();

  const features = [
    {
      icon: <FaLanguage className="w-6 h-6" />,
      title: "Smart Translation",
      description: "AI-powered translations that maintain your brand voice across languages"
    },
    {
      icon: <FaRobot className="w-6 h-6" />,
      title: "Automated Workflow",
      description: "Schedule and automate content across multiple social platforms"
    },
    {
      icon: <FaChartLine className="w-6 h-6" />,
      title: "Performance Analytics",
      description: "Track engagement across languages and platforms in real-time"
    },
    {
      icon: <FaGlobe className="w-6 h-6" />,
      title: "Global Reach",
      description: "Expand your audience with localized content that resonates"
    }
  ];

  return (
    <section className="lg:flex min-h-screen">
      {/* Left Section - Enhanced Content */}
      <div className="bg-gradient-to-br from-[#6833ff] to-[#5346D9] w-full lg:w-7/12 min-h-[550px] lg:min-h-screen relative overflow-hidden">
        <div className="container mx-auto px-4 lg:px-10 py-8 lg:py-16 text-white relative z-10">
          {/* Logo and Back Button */}
          <div className="flex items-center justify-between mb-12">
            <img
              src={logo}
              alt="Dubmate"
              className="w-[100px] sm:w-[200px]"
            />
            <button
              onClick={() => navigate(pathnames.home)}
              className="lg:hidden w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <FaArrowLeft className="w-4 h-4" />
            </button>
          </div>

          {/* Main Content */}
          <div className="max-w-2xl mx-auto lg:mx-0">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl lg:text-6xl font-bold leading-tight mb-6"
            >
              Transform Your <span className="text-[#8C84FF]">Global Presence</span> with Smart Content Translation
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg lg:text-xl text-white/90 mb-12"
            >
              Manage multilingual social media content effortlessly. Save time, maintain brand consistency, and connect with audiences worldwide.
            </motion.p>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="bg-white/10 backdrop-blur rounded-xl p-6"
                >
                  <div className="flex items-start gap-4">
                    <div className="mt-1 text-[#8C84FF]">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-white/80 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Stats */}
            <div className="flex flex-wrap justify-start gap-12">
              {[
                { value: "90%", label: "Workload Reduction" },
                { value: "24/7", label: "Automated Publishing" },
                { value: "100+", label: "Language Pairs" }
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-2xl lg:text-3xl font-bold text-[#8C84FF] mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-white/80">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Background Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-[#5346D9] to-transparent" />
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-[#8C84FF] opacity-10 blur-3xl rounded-full transform translate-x-1/2 -translate-y-1/2" />
      </div>

      {/* Right Section - Auth Button */}
      <div className="bg-[#F2F0FA] w-full lg:w-5/12 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-gray-600">
              Join thousands of content creators who are going global with Dubmate
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Button
              onClick={() => loginWithRedirect()}
              label='Authenticate with Dubmate'
            // className="bg-gradient-to-r from-[#8C84FF] to-[#6158E0] hover:from-[#7A71FF] hover:to-[#5346D9] transform hover:-translate-y-1 transition-all duration-200"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SignIn;